import { useState, useEffect } from "react";
import { Box, Tabs, Tab, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { styled } from "@mui/system";
import { CustomSelect, FileUpload } from "../../../components-new/common";
import {
  useListOfScriptsQuery,
  useUploadFileQuery,
  useCommandLineFilesQuery,
  useCommandLineUpdateQuery,
  useCommandLineUploadQuery,
  useScriptUpdateQuery,
} from "../../../useQuery";
import { useNotification } from "../../../hooks";

const StyledButton = styled(Button)(() => ({
  textTransform: "capitalize",
  background: "#FF5336",
  borderRadius: "8px",
  padding: "11px 35px",
  width: "125px",
  height: "42px",
  "&:disabled": {
    backgroundColor: "#cccccc",
    color: "#666666",
  },
}));

const StyledTabs = styled(Tabs)({
  backgroundColor: "transparent",
  width: "auto",
  "& .MuiTabs-flexContainer": {
    columnGap: "2px",
  },
  "& .MuiTabs-indicator": {
    background: "#FF5336",
  },
});

const StyledTab = styled(Tab)({
  background: "#ffffff",
  borderBottom: "1px solid #E2E6F3",
  color: "#605555",
  textTransform: "capitalize",
  fontSize: "14px",
  "&.Mui-selected": {
    color: "#000000",
  },
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

const Script = ({ item }) => {
  const [value, setValue] = useState(0);
  const [scriptType, setScriptType] = useState("javascript");
  const [selectedCmdFile, setSelectedCmdFile] = useState("");
  const [selectedScriptFile, setSelectedScriptFile] = useState(item.scriptfile || "");

  const notify = useNotification();

  const { data: scriptsList, refetch: refetchScripts } = useListOfScriptsQuery();
  const { data: cmdFilesList, refetch: refetchCmdFiles } = useCommandLineFilesQuery();

  const { mutate: uploadFile } = useUploadFileQuery();
  const { mutate: uploadCmdFile } = useCommandLineUploadQuery();
  const { mutate: updateCmdFile } = useCommandLineUpdateQuery();
  const { mutate: updateScriptFile } = useScriptUpdateQuery();

  useEffect(() => {
    const refetch = scriptType === "javascript" ? refetchScripts : refetchCmdFiles;
    refetch();
  }, [scriptType, refetchScripts, refetchCmdFiles]);

  const handleTabChange = (_, newValue) => setValue(newValue);

  const handleScriptTypeChange = (event) => {
    setScriptType(event.target.value);
    setValue(0);
    setSelectedCmdFile("");
    setSelectedScriptFile("");
  };

  const handleUpload = (file) => {
    if (!file) return;

    const payload = {
      file,
      testStepId: item.idtest_step,
      testCaseId: item.Test_Case_Id,
    };

    if (scriptType === "javascript") {
      uploadFile(payload, {
        onSuccess: () => {
          notify("Script file uploaded successfully", "success");
        },
        onError: (error) => {
          notify(error?.message || "Failed to upload script file", "error");
        },
      });
    } else {
      uploadCmdFile(payload, {
        onSuccess: () => {
          notify("Command line file uploaded successfully", "success");
        },
        onError: (error) => {
          notify(error?.message || "Failed to upload command line file", "error");
        },
      });
    }
  };

  const handleFileSelect = () => {
    const payload = {
      testStepId: item.idtest_step,
      testCaseId: item.Test_Case_Id,
    };

    if (scriptType === "javascript") {
      updateScriptFile(
        { ...payload, scriptfile: selectedScriptFile },
        {
          onSuccess: () => {
            notify("Script file selected successfully", "success");
          },
          onError: (error) => {
            notify(error?.message || "Failed to select script file", "error");
          },
        },
      );
    } else {
      updateCmdFile(
        { ...payload, cmdfile: selectedCmdFile },
        {
          onSuccess: () => {
            notify("Command line file selected successfully", "success");
          },
          onError: (error) => {
            notify(error?.message || "Failed to select command line file", "error");
          },
        },
      );
    }
  };

  const getSelectOptions = (list) =>
    (list || []).map((item) => ({
      label: item.FileName,
      value: item.FileName,
    }));

  const isScriptType = scriptType === "javascript";
  const selectedFile = isScriptType ? selectedScriptFile : selectedCmdFile;
  const options = getSelectOptions(isScriptType ? scriptsList : cmdFilesList);

  return (
    <Box pr={3}>
      <Box mb={3}>
        <FormControl sx={{ minWidth: 200, mt: 2 }}>
          <InputLabel>Script Type</InputLabel>
          <Select value={scriptType} label="Script Type" onChange={handleScriptTypeChange}>
            <MenuItem value="javascript">JavaScript</MenuItem>
            <MenuItem value="commandline">Command Line</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "#d1dbff" }}>
        <StyledTabs value={value} onChange={handleTabChange}>
          <StyledTab label={`Select ${isScriptType ? "Script" : "Command Line"}`} />
          <StyledTab label={`Upload ${isScriptType ? "Script" : "Command Line"}`} />
        </StyledTabs>
      </Box>

      <Box>
        <CustomTabPanel value={value} index={0}>
          <Box mt={3} display="flex" alignItems="center" gap={3}>
            <CustomSelect
              width="318px"
              label={isScriptType ? "Select Script File" : "Select Command Line File"}
              options={options}
              value={selectedFile}
              onChange={(e) =>
                isScriptType
                  ? setSelectedScriptFile(e.target.value)
                  : setSelectedCmdFile(e.target.value)
              }
            />
            <StyledButton
              variant="contained"
              onClick={handleFileSelect}
              disabled={!selectedFile}
              sx={{ mt: 2 }}
            >
              Select
            </StyledButton>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <FileUpload handleUpload={handleUpload} />
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default Script;
